import { type CSSProperties, type NestedCSSProperties } from 'typestyle/lib/types';
import appLogo from '../../../assets/images/appLogo.svg';
import StyleConstants from '../../styles.constants';

export const navigation = (collapsed: boolean): CSSProperties => ({
  position: 'relative',
  background: `linear-gradient(160deg, ${StyleConstants.DARK_BLUE_COLOR} 0%, ${StyleConstants.MEDIUM_BLUE_COLOR} 100%)`,
  width: collapsed ? StyleConstants.NAVIGATION_COLLAPSED_WIDTH : StyleConstants.NAVIGATION_WIDTH,
  flexShrink: 0,
  boxSizing: 'border-box',
  overflowX: 'hidden',
  overflowY: 'auto',
  height: '100vh',
  paddingLeft: collapsed ? 12 : 16,
  paddingRight: collapsed ? 12 : 16,
  transitionDuration: '250ms',
  transitionProperty: 'width',
  transitionTimingFunction: 'ease-out',
  boxShadow: '1px 2px 8px rgba(0, 0, 0, 0.25)',
});

export const navigationMobile = (open: boolean, collapsed: boolean): CSSProperties => ({
  transitionDuration: '300ms',
  transitionProperty: 'left',
  overflowY: 'auto',
  position: 'absolute',
  top: 0,
  left: open ? 0 : -(collapsed ? StyleConstants.NAVIGATION_COLLAPSED_WIDTH : StyleConstants.NAVIGATION_WIDTH),
  zIndex: 10,
});

export const glow: CSSProperties = {
  pointerEvents: 'none',
  position: 'absolute',
  top: -96,
  right: -64,
  height: 256,
  width: 256,
  borderRadius: '50%',
  opacity: 0.25,
  filter: 'blur(64px)',
  background: StyleConstants.ORANGE_COLOR,
};

export const divider: CSSProperties = {
  height: 1,
  width: '100%',
  backgroundColor: StyleConstants.NAVIGATION_DIVIDER_COLOR,
};

export const menuSection: CSSProperties = {
  flex: 1,
  paddingTop: 16,
  paddingBottom: 16,
  width: '100%',
};

export const menuHeader = (collapsed: boolean): CSSProperties => ({
  display: collapsed ? 'none' : 'block',
  paddingLeft: 12,
  paddingBottom: 8,
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '1.5px',
  color: StyleConstants.NAVIGATION_FAINT_TEXT_COLOR,
});

export const logoutSection: CSSProperties = {
  paddingTop: 12,
  paddingBottom: 12,
  width: '100%',
};

export const brandLockup = (collapsed: boolean): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  width: '100%',
  padding: collapsed ? '10px 6px' : '12px 14px',
  marginBottom: 12,
  borderRadius: 12,
  backgroundColor: StyleConstants.NAVIGATION_SURFACE_COLOR,
});

export const whyellowLogo = (collapsed: boolean): CSSProperties => ({
  height: collapsed ? 20 : 42,
  width: '100%',
  backgroundImage: `url(${appLogo})`,
  backgroundSize: 'contain',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
});

export const footer = (collapsed: boolean): NestedCSSProperties => ({
  display: 'flex',
  flexDirection: collapsed ? 'column' : 'row',
  alignItems: 'center',
  justifyContent: collapsed ? 'center' : 'space-between',
  gap: 8,
  width: '100%',
  paddingTop: 12,
  paddingBottom: 8,
});

export const flags: CSSProperties = {
  height: 20,
  width: 20,
  borderRadius: '50%',
  objectFit: 'cover',
  cursor: 'pointer',
  opacity: 0.75,
  transition: 'opacity 150ms ease, box-shadow 150ms ease',
};

export const activeFlag: CSSProperties = {
  opacity: 1,
  boxShadow: `0 0 0 2px ${StyleConstants.ORANGE_COLOR}`,
};

export const collapseToggle: NestedCSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 28,
  width: 28,
  flexShrink: 0,
  padding: 0,
  border: 'none',
  borderRadius: '50%',
  cursor: 'pointer',
  color: StyleConstants.WHITE_COLOR,
  backgroundColor: 'rgba(255, 255, 255, 0.12)',
  transition: 'background-color 150ms ease',
  $nest: {
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.20)',
    },
  },
};

export const versionText = (collapsed: boolean): CSSProperties => ({
  display: collapsed ? 'none' : 'block',
  fontFamily: StyleConstants.PRIMARY_FONT_FAMILY,
  fontSize: 10,
  color: StyleConstants.NAVIGATION_FAINT_TEXT_COLOR,
  paddingBottom: 12,
  letterSpacing: '0.5px',
  width: '100%',
});
